import React from "react";
import { store } from "@redux/store";
import actions from "@redux/EditableTable/actions";
import EquipmentLookup from "@containers/Lookup/EquipmentLookup";
import WorkorderLink from "@containers/facility/Workorder/WorkorderLink";
import { formatWorkorderRef } from "@/utils/entity-ref";

import _ from "lodash";

import moment from "moment";
import ProjectLookup2 from "@containers/Lookup/ProjectLookup2";
import { Badge, Input, Tooltip, Tag, Typography } from "antd";
import WorkorderNotification from "@containers/facility/Workorder/WorkorderNotification";
import {
  getWorkOrderDomainDefault,
  getWorkOrderDomainValueEnum,
  getWorkOrderInitialStatus,
  getWorkOrderStatusValueEnum,
  isWorkOrderTerminalStatus,
} from "@/services/semanticCapabilities";

const { Text } = Typography;

const { TextArea } = Input;
const DEFAULT_WORKORDER_STATUS = getWorkOrderInitialStatus();

/** 与工单 API 的日期字段兼容；缺省或非法时不渲染（避免 moment(undefined) 变成“今天”）。 */
const formatTableDate = (v) => {
  if (v == null || v === "") return "";
  const d = moment(v);
  return d.isValid() ? d.format("YYYY-MM-DD") : "";
};

export const WORKORDER_STATUS_THEME = {
  Completed: {
    text: "Completed",
    backgroundColor: "#dcfce7",
    borderColor: "#16a34a",
    color: "#14532d",
  },
  Verifying: {
    text: "Verifying",
    backgroundColor: "#dbeafe",
    borderColor: "#2563eb",
    color: "#1d4ed8",
  },
  "On-Hold": {
    text: "On-Hold",
    backgroundColor: "#fef3c7",
    borderColor: "#d97706",
    color: "#92400e",
  },
  InProgress: {
    text: "InProgress",
    backgroundColor: "#fef2f2",
    borderColor: "#f97316",
    color: "#c2410c",
  },
  "Not Started": {
    text: "Not Started",
    backgroundColor: "#f8fafc",
    borderColor: "#94a3b8",
    color: "#475569",
  },
};

export const WORKORDER_PRIORITY_THEME = {
  Urgent: {
    text: "Urgent",
    backgroundColor: "#fee2e2",
    borderColor: "#ef4444",
    color: "#b91c1c",
  },
  Important: {
    text: "Important",
    backgroundColor: "#fff7e6",
    borderColor: "#faad14",
    color: "#ad6800",
  },
  Low: {
    text: "Low",
    backgroundColor: "#dcfce7",
    borderColor: "#22c55e",
    color: "#15803d",
  },
};

export const WORKORDER_WRAPPER_STATUS_THEME = {
  Open: {
    text: "Open",
    backgroundColor: "#f0f9ff",
    borderColor: "#38bdf8",
    color: "#0369a1",
  },
  "In Progress": {
    text: "In Progress",
    backgroundColor: "#fefce8",
    borderColor: "#facc15",
    color: "#854d0e",
  },
  Completed: {
    text: "Completed",
    backgroundColor: "#dcfce7",
    borderColor: "#16a34a",
    color: "#14532d",
  },
  Cancelled: {
    text: "Cancelled",
    backgroundColor: "#f1f5f9",
    borderColor: "#94a3b8",
    color: "#475569",
  },
};

const getWorkorderTagStyle = (theme) => ({
  backgroundColor: theme.backgroundColor,
  borderColor: theme.borderColor,
  color: theme.color,
  fontWeight: 600,
  letterSpacing: 0,
  margin: 0,
  fontSize: 11,
  lineHeight: "18px",
  padding: "0 7px",
  minWidth: 0,
  textAlign: "center",
});

export const WorkorderProps = {
  title: "Workorder",
  description: "",
  name: "workorder",
  tableName: "kpiworkorders",
};

export const WorkorderColumns = () => [
  // Issue - 固定在左侧
  {
    title: "Issue",
    dataIndex: "issue",
    key: "issue",
    width: 220,
    fixed: "left",
    search: false,
    edit: true,
    sorter: (a, b) => {
      if (a._isWrapperRow || b._isWrapperRow) return 0;
      return a.issue?.localeCompare(b.issue);
    },
    formItemProps: {
      rules: [
        {
          required: true,
          message: "required",
        },
      ],
    },
    render: (text, record, _, action) => {
      if (record._isWrapperRow) {
        return (
          <span>
            <Text type="secondary" style={{ fontSize: 11, fontWeight: 500, marginRight: 6, color: "#6b7280" }}>PM</Text>
            <Text strong style={{ fontSize: 13 }}>{record._wrapper_label}</Text>
          </span>
        );
      }
      return <WorkorderLink id={record.id} text={text} action={action} />;
    },
    renderFormItem: () => <TextArea rows={1} />,
    editable: (_, record) => !record._isWrapperRow,
  },
  // Equipment（多选：equipment_name 为后端 join 的全部设备名；dataIndex 保留 equipment 作为列设置键）
  {
    title: "Equipment",
    dataIndex: "equipment",
    key: "equipment",
    edit: true,
    width: 160,
    type: "select",
    valueType: "select",
    ellipsis: { showTitle: false },
    sorter: (a, b) => {
      if (a._isWrapperRow || b._isWrapperRow) return 0;
      return (a.equipment_name || "").localeCompare(b.equipment_name || "");
    },
    hideInTable: window.innerWidth < 760,
    render: (text, record) => {
      if (record._isWrapperRow) {
        const list = record._equipment_list || [];
        if (!list.length) return null;
        return (
          <Tooltip placement="topLeft" title={list.join(", ")}>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
              {list.map((name) => (
                <Tag key={name} style={{ margin: 0 }}>{name}</Tag>
              ))}
            </div>
          </Tooltip>
        );
      }
      const names = _.get(record, "equipment_name");
      if (!names) return null;
      return (
        <Tooltip placement="topLeft" title={names}>
          <div className="workorder-description-ellipsis">{names}</div>
        </Tooltip>
      );
    },
    renderFormItem: (_, { isEditable }) => {
      let record = _.entity?.equipment_name ? _.entity.equipment_name : "";
      return <EquipmentLookup record={record} />;
    },
    editable: (_, record) => !record._isWrapperRow,
  },
  // Description
  {
    title: "Description",
    dataIndex: "description",
    key: "description",
    width: 200,
    edit: true,
    required: false,
    search: false,
    hideInTable: false,
    render: (text) => (
      <Tooltip title={text}>
        <div className="workorder-description-ellipsis">
          {text}
        </div>
      </Tooltip>
    ),
    renderFormItem: () => <TextArea rows={1} />,
  },
  // Responsible Party
  {
    title: "Responsible Party",
    dataIndex: "responsible_party",
    key: "responsible_party",
    edit: true,
    width: 80,
    sorter: (a, b) => a.responsible_party?.localeCompare(b.responsible_party),
    render: (text, record) => {
      const responsibleParty = record.responsible_party;
      return (
        <Tooltip placement="topLeft" title={responsibleParty}>
          <>{responsibleParty}</>
        </Tooltip>
      );
    },
  },
  // Assign To
  {
    title: "Assign To",
    dataIndex: "assignTo_name",
    key: "assignTo_name",
    edit: true,
    width: 80,
    sorter: (a, b) => a.assignTo_name?.localeCompare(b.assignTo_name),
  },
  // Status
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    edit: true,
    width: 100,
    align: "left",
    editable: (_, record) => !record._isWrapperRow,
    render: (text, record) => {
      if (record._isWrapperRow) {
        const wStatus = record.status || "Open";
        const wTheme = WORKORDER_WRAPPER_STATUS_THEME[wStatus] || {
          text: wStatus,
          backgroundColor: "#f8fafc",
          borderColor: "#94a3b8",
          color: "#475569",
        };
        return (
          <Tag style={getWorkorderTagStyle(wTheme)}>
            {wTheme.text}
          </Tag>
        );
      }
      const dueDate = record?.due_date;
      const statusText = record?.status || DEFAULT_WORKORDER_STATUS;
      const theme =
        WORKORDER_STATUS_THEME[statusText] ||
        WORKORDER_STATUS_THEME[DEFAULT_WORKORDER_STATUS] || {
          text: statusText,
          backgroundColor: "#f8fafc",
          borderColor: "#94a3b8",
          color: "#475569",
        };
      const tagStyle = getWorkorderTagStyle(theme);
      const isOverDue =
        !isWorkOrderTerminalStatus(statusText) &&
        dueDate &&
        moment(dueDate).isValid() &&
        moment(dueDate).isBefore(moment(), "day");

      const mainTag = <Tag style={tagStyle}>{theme.text}</Tag>;

      if (!isOverDue) {
        return mainTag;
      }

      return (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
            lineHeight: "20px",
          }}
        >
          {mainTag}
          <Tag
            color="#ff4d4f"
            style={{
              backgroundColor: "#fff1f0",
              borderColor: "#ff4d4f",
              color: "#a8071a",
              fontWeight: 600,
              margin: 0,
              fontSize: 11,
              lineHeight: "18px",
              padding: "0 7px",
            }}
          >
            Over Due
          </Tag>
        </div>
      );
    },
    sorter: (a, b) => a.status?.localeCompare(b.status),
    valueEnum: getWorkOrderStatusValueEnum(),
    formItemProps: {
      initialValue: "",
    },
  },
  // Priority
  {
    title: "Priority",
    dataIndex: "priority",
    key: "priority",
    width: 90,
    align: "left",
    edit: true,
    sorter: (a, b) => a.priority?.localeCompare(b.priority),
    render: (text, record) => {
      const priorityText = record?.priority || "-";
      const theme = WORKORDER_PRIORITY_THEME[priorityText] || {
        text: priorityText,
        backgroundColor: "#f8fafc",
        borderColor: "#cbd5e1",
        color: "#475569",
      };

      return (
        <Tag style={getWorkorderTagStyle(theme)}>
          {theme.text}
        </Tag>
      );
    },
    valueEnum: getWorkOrderDomainValueEnum("priority"),
    formItemProps: {
      initialValue: getWorkOrderDomainDefault("priority"),
    },
  },
  // Energy Impact
  {
    title: "Energy Impact",
    dataIndex: "energy_impact",
    key: "energy_impact",
    responsive: ['md'],
    edit: true,
    width: 80,
    align: "left",
    sorter: (a, b) => a.energy_impact?.localeCompare(b.energy_impact),
    valueEnum: getWorkOrderDomainValueEnum("energy_impact"),
    formItemProps: {
      initialValue: getWorkOrderDomainDefault("energy_impact"),
    },
  },
  // Complete Date
  {
    title: "Complete Date",
    dataIndex: "complete_date",
    edit: true,
    type: "date",
    width: 90,
    hideInTable: false,
    className: "date-editor",
    key: "complete_date",
    align: "right",
    sorter: (a, b) => a.complete_date?.localeCompare(b.complete_date),
    render: (_, record) => formatTableDate(record?.complete_date),
    renderFormItem: () => <Input type={"date"} />,
  },
  // Report Date (显示用)
  {
    title: "Report Date",
    dataIndex: "reportDate",
    key: "reportDate",
    width: 90,
    hideInTable: false,
    editable: false,
    importIngore: true,
    align: "right",
    sorter: (a, b) => a.reportDate?.localeCompare(b.reportDate),
    render: (text, record) => formatTableDate(record?.reportDate),
  },
  // Due Date
  {
    title: "Due Date",
    dataIndex: "due_date",
    key: "due_date",
    width: 90,
    hideInTable: false,
    editable: false,
    importIngore: true,
    align: "right",
    sorter: (a, b) => a.due_date - b.due_date,
    render: (text, record) => formatTableDate(record?.due_date),
  },
  // Modify Date
  {
    title: "Modify Date",
    dataIndex: "modified_date",
    key: "modified_date",
    responsive: ['md'],
    width: 90,
    editable: false,
    search: false,
    align: "right",
    sorter: (a, b) => a.modified_date - b.modified_date,
    importIngore: true,
    render: (text, record) => formatTableDate(record?.modified_date),
  },
  // Comments - 固定在右侧
  {
    title: "Comments",
    valueType: "option",
    width: 50,
    fixed: "right",
    importIngore: true,
    render: (text, record, _, action) => {
      if (record._isWrapperRow) return null;
      return (
        <div className="pl-2">
          <WorkorderNotification record={record} />
          {record?.has_attachment ? (
            <Badge>
              <i
                className="fas fa-paperclip text-primary text-2xl "
                style={{ color: '#3A3F63', fontSize: "12px", marginLeft: 4 }}
              ></i>
            </Badge>
          ) : (
            ""
          )}
        </div>
      );
    },
  },
  // ========== 以下是默认隐藏的列 ==========
  // key (id)
  {
    title: "ID",
    dataIndex: "id",
    key: "id",
    editable: false,
    search: false,
    importIngore: true,
    hideInTable: false,
    width: 80,
    render: (value) => formatWorkorderRef(value),
    sorter: (a, b) => a.id - b.id,
  },
  // Equipment Name (用于表单)
  {
    title: "Equipment",
    dataIndex: "equipment_name",
    key: "equipment_name",
    edit: true,
    hideInTable: true,
    type: "select",
    valueType: "select",
    sorter: (a, b) => (a.equipment_name || "").localeCompare(b.equipment_name || ""),
    render: (text, record) => _.get(record, "equipment_name"),
    renderFormItem: () => <EquipmentLookup />,
  },
  // Recommended Action
  {
    title: "Recommended Action",
    dataIndex: "recommended_action",
    key: "recommended_action",
    edit: true,
    width: 100,
    sorter: (a, b) =>
      a?.recommended_action?.localeCompare(b?.recommended_action),
    renderFormItem: () => <TextArea rows={1} />,
    render: (text, record) => {
      const recommendedAction = record.recommended_action;
      return (
        <Tooltip placement="topLeft" title={recommendedAction}>
          <>{recommendedAction}</>
        </Tooltip>
      );
    },
  },
  // WorkOrder Number
  {
    title: "WorkOrder Number",
    dataIndex: "workordernumber",
    key: "workordernumber",
    width: 80,
    search: false,
    edit: true,
    sorter: (a, b) => a.workordernumber?.localeCompare(b.workordernumber),
  },
  // Project
  {
    title: "Project",
    dataIndex: "project_name",
    key: "project",
    edit: true,
    width: 100,
    type: "select",
    valueType: "select",
    hideInTable: true,
    render: (text, record) => _.get(record, "project_name.name"),
    renderFormItem: (_, { isEditable }) => {
      let record = _.entity.project_name ? _.entity.project_name.name : "";
      return <ProjectLookup2 record={record} />;
    },
  },
  // Reported By
  {
    title: "Reported By",
    dataIndex: "reportBy",
    hideInTable: false,
    width: 90,
    className: "date-editor",
    key: "reportBy",
    render: (text) => (
      <Tooltip title={text}>
        <div style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          width: '100%'
        }}>
          {text}
        </div>
      </Tooltip>
    ),
    renderFormItem: () => <Input />,
  },
  // Report Date (编辑用)
  {
    title: "Report Date",
    dataIndex: "reportDate",
    edit: true,
    type: "date",
    width: 90,
    className: "date-editor",
    key: "reportDate_edit",
    sorter: (a, b) => a.reportDate?.localeCompare(b.reportDate),
    renderFormItem: () => <Input type={"date"} />,
    hideInTable: true,
  },
  // Create Date
  {
    title: "Create Date",
    dataIndex: "created_date",
    key: "created_date",
    width: 90,
    hideInTable: false,
    editable: false,
    importIngore: true,
    align: "right",
    sorter: (a, b) => a.created_date - b.created_date,
    render: (text, record) => formatTableDate(record?.created_date),
  },
  // Created By
  {
    title: "Created By",
    dataIndex: "createdby_name",
    key: "createdby_name",
    editable: false,
    width: 90,
    search: false,
    importIngore: true,
    hideInTable: false,
    render: (text, record) => {
      const createdByName = record.createdby_name;
      return (
        <Tooltip placement="topLeft" title={createdByName}>
          <>{createdByName}</>
        </Tooltip>
      );
    },
  },
  // Modified By
  {
    title: "Modified By",
    dataIndex: "lastModified_name",
    key: "lastModified_name",
    editable: false,
    search: false,
    width: 90,
    importIngore: true,
    hideInTable: false,
    render: (text, record) => {
      const lastModifiedName = record.lastModified_name;
      return (
        <Tooltip placement="topLeft" title={lastModifiedName}>
          <>{lastModifiedName}</>
        </Tooltip>
      );
    },
  },
  // modified_date (隐藏字段)
  {
    title: "modified_date",
    dataIndex: "modified_date",
    key: "modified_date_hidden",
    editable: false,
    search: false,
    importIngore: true,
    hideInTable: true,
  },
];
